import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Modal } from '@material-ui/core';
import BackButton from '../BackButton/BackButton';
import styles from './AgsnSwapModal.module.css';
import moment from 'moment';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import {
  AGGREGATE_STATUS,
  API_RESOURCE_URLS,
  DISPLAY_MESSAGES,
  RESOURCE_TYPE,
  USER_OPERATIONS,
} from '../../constants';
import { Table, CommentFormModal } from '..';
import { buildErrorMessage, fetchSwappableAgsn } from '../../apis/calls';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import {
  aggregateReasons,
  aggregateAgencies,
  statusStringMap,
} from '../../staticData';
import { API } from '../../apis/api';
import { withAllowedOperationsProvider } from '../../hocs';

const useStyles = makeStyles(theme => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    border: '2px solid #000',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },
}));

const AgsnSwapModal = ({
  open,
  setIsAgsnSwapModalOpen,
  backButtonName,
  setAgsnSwapCount,
  selectedAgsn,
  loadLinedOutAgsnOfSelectedPlantAndLineForDate,
  loadAgsnsForADate,
  selectedDate,
}) => {
  const classes = useStyles();
  const ROW_HEIGHT = 38;
  const RESOURCE_OPERATION = USER_OPERATIONS.AGGREGATE_LINE_OUT;
  const { showPopup } = usePopupManager() || true;
  // eslint-disable-next-line no-unused-vars
  const [hasActiveEdits, setHasActiveEdits] = useState(false);
  const [aggregateLine] = usePlantAndLinesStore().aggregateLineState;
  const [plant] = usePlantAndLinesStore().plantState;
  const [rowsData, setRowsData] = useState({
    loading: false,
    data: [],
    filteredData: [],
  });
  const { showSuccess, showLoading, closePopUp } = usePopupManager();
  const [isSwapModalOpen, setIsSwapModalOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const baseDefaultColumns = [
    {
      width: 150,
      title: 'Destination',
      field: 'destination',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 120,
      title: 'AgSN',
      field: 'agsn',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 120,
      title: 'ASN',
      field: 'parentAsn',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 120,
      title: 'VC',
      field: 'parentMaterialNumber',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 290,
      title: 'VC description',
      field: 'parentMaterialDescription',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 180,
      title: 'Aggregate Status',
      field: 'status',
      render: row => {
        if (row.status === AGGREGATE_STATUS.ASN_GENERATED)
          return (
            <span style={{ opacity: 0.5 }}>{statusStringMap[row.status]}</span>
          );
        return statusStringMap[row.status];
      },
      enableSearch: true,
      enableFilter: true,
    },
  ].filter(c => c);

  const closeModal = () => setIsSwapModalOpen(false);
  const openModal = selectedRows => {
    setIsSwapModalOpen(true);
    setSelectedRows(selectedRows);
  };
  const handleClose = () => {
    setIsAgsnSwapModalOpen(false);
  };
  const sortByAgsn = (a, b) => {
    if (a.agsn > b.agsn) return 1;
    if (a.agsn < b.agsn) return -1;
    return 0;
  };

  const loadAgsnForSwap = async (plantId, lineId, materialId) => {
    if (!plantId) return;
    if (!lineId) return;
    if (!materialId) return;
    try {
      setRowsData(prevState => ({ ...prevState, loading: true }));
      const response = await fetchSwappableAgsn(plantId, lineId, materialId);
      const { data: swappableAgsn } = response;
      const filteredAgsn = swappableAgsn.sort(sortByAgsn);
      setRowsData({
        loading: false,
        data: filteredAgsn,
        filteredData: filteredAgsn,
      });
    } catch (err) {
      setRowsData({
        loading: false,
        data: [],
        filteredData: [],
      });
      console.error(buildErrorMessage(err));
      showPopup({
        error: err,
        contextText: DISPLAY_MESSAGES.GET_AGGREGATE_SWAPPABLE_AGSN_FAILED,
      });
      setRowsData(prevState => ({ ...prevState, loading: false }));
    }
  };

  const swapAgsn = async (rows, reason, agency, comment) => {
    const agsn = rows[0];
    try {
      showLoading();
      const payload = {
        id: agsn.id,
        reason,
        agency,
        comment,
      };
      await API.patch(
        API_RESOURCE_URLS.aggregateSwap(
          plant.value,
          aggregateLine.value,
          selectedAgsn[0].id,
        ),
        payload,
      );
      setIsAgsnSwapModalOpen(false);
      loadLinedOutAgsnOfSelectedPlantAndLineForDate();
      loadAgsnsForADate(plant?.value, aggregateLine?.value, selectedDate);
      closePopUp();

      showSuccess({
        contextText: DISPLAY_MESSAGES.AGSN_STATUS_SWAP,
        info: '',
      });
    } catch (err) {
      console.error(buildErrorMessage(err));
      showPopup({
        error: err,
        contextText: DISPLAY_MESSAGES.AGGREGATE_SWAP_FAILED,
      });
    }
  };

  useEffect(() => {
    loadAgsnForSwap(plant?.value, aggregateLine?.value, selectedAgsn[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAgsn, plant?.value, aggregateLine?.value]);

  return (
    <>
      <Modal open={open} onClose={handleClose} className={classes.modal}>
        <div className={styles.formContainer}>
          <div className={styles.backButtonAndFormNameWrapper}>
            <div className={styles.backButtonContainer}>
              <BackButton
                action={backButtonName}
                handleClick={handleClose}
                materialGroup="Swap"
              />
            </div>
            <span className={styles.selectedAgsnStyle}>
              AgSN {selectedAgsn[0].agsn}
            </span>
          </div>
          <div
            style={{
              padding: '1em',
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
            }}
          >
            <div className={styles.tableContainer}>
              {baseDefaultColumns.length && (
                <Table
                  columns={[...baseDefaultColumns]}
                  defaultColumns={[...baseDefaultColumns]}
                  rowHeight={ROW_HEIGHT}
                  rows={rowsData.data}
                  setHasActiveEdits={setHasActiveEdits}
                  isDataLoading={rowsData.loading}
                  displayHash
                  setAsnCount={setAgsnSwapCount}
                  primaryAction={{
                    name: 'SWAP',
                    actionFn: selectedRows => openModal(selectedRows),
                    authOperation: RESOURCE_OPERATION,
                    shouldEnable: selectedRows =>
                      selectedRows.length === 1 &&
                      selectedRows.every(
                        row => row.status !== AGGREGATE_STATUS.DISPATCHED,
                      ),
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </Modal>
      {isSwapModalOpen ? (
        <CommentFormModal
          open={isSwapModalOpen}
          handleClose={closeModal}
          title="Swap"
          rowsData={selectedRows}
          reasons={aggregateReasons}
          agencies={aggregateAgencies}
          lineBackgroundColor="#82C1A7"
          handleSubmit={(rows, reason, agency, comment) => {
            swapAgsn(rows, reason, agency, comment);
          }}
        />
      ) : null}
    </>
  );
};

AgsnSwapModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setIsAgsnSwapModalOpen: PropTypes.func.isRequired,
  backButtonName: PropTypes.string.isRequired,
  setAgsnSwapCount: PropTypes.func.isRequired,
  selectedAgsn: PropTypes.array.isRequired,
  loadLinedOutAgsnOfSelectedPlantAndLineForDate: PropTypes.func.isRequired,
  loadAgsnsForADate: PropTypes.func.isRequired,
  selectedDate: PropTypes.objectOf(moment),
};

export default withAllowedOperationsProvider(
  AgsnSwapModal,
  true,
  RESOURCE_TYPE.MATERIAL,
);
